/**
 * Webhooks — Wave 1b / W1b-05.
 *
 * TanStack Query hooks for `/api/v1/admin/settings/webhooks`. Tenant token
 * (audience `argusai`) flows automatically via the apiClient.
 *
 * UI lives at packages/web/src/routes/admin/settings/api.tsx (Webhooks tab).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type WebhookDeliveryStatus =
  | 'queued'
  | 'success'
  | 'failed'
  | 'retrying'
  | 'dead_letter';

export interface WebhookSummary {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  signingSecretPrefix: string;
  signingSecretRotatedAt: string;
  httpMethod: string;
  customHeaders: Record<string, string>;
  retryMaxAttempts: number;
  retryBackoffSeconds: number;
  timeoutSeconds: number;
  mtlsClientCertId: string | null;
  lastTestAt: string | null;
  lastTestResult: {
    success: boolean;
    statusCode: number | null;
    latencyMs: number | null;
    errorMessage: string | null;
  } | null;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: string | null;
  successCount24h: number;
  failureCount24h: number;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  event: string;
  dispatchedAt: string;
  attemptCount: number;
  statusCode: number | null;
  latencyMs: number | null;
  status: WebhookDeliveryStatus;
  errorMessage: string | null;
  payloadSize: number | null;
  payloadSummary: string | null;
  isTest: boolean;
  createdAt: string;
}

export interface WebhookListResponse {
  webhooks: WebhookSummary[];
  total: number;
}

export interface WebhookDetailResponse {
  webhook: WebhookSummary;
  recentDeliveries: WebhookDelivery[];
}

export interface WebhookDeliveryListResponse {
  deliveries: WebhookDelivery[];
  total: number;
  limit: number;
  offset: number;
}

export interface EventCatalogResponse {
  categories: Array<{
    category: string;
    events: Array<{ key: string; description: string }>;
  }>;
  totalEvents: number;
}

export interface WebhookCreateBody {
  name: string;
  url: string;
  events: string[];
  enabled?: boolean;
  httpMethod?: 'POST' | 'PUT' | 'PATCH';
  customHeaders?: Record<string, string>;
  retryMaxAttempts?: number;
  retryBackoffSeconds?: number;
  timeoutSeconds?: number;
  mtlsClientCertId?: string | null;
  reason?: string;
}

export interface WebhookCreateResponse {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  signingSecret: string;
  signingSecretPrefix: string;
  createdAt: string;
}

export interface WebhookUpdateBody {
  name?: string;
  url?: string;
  events?: string[];
  enabled?: boolean;
  customHeaders?: Record<string, string>;
  retryMaxAttempts?: number;
  retryBackoffSeconds?: number;
  timeoutSeconds?: number;
  reason?: string;
}

export interface WebhookRotateResponse {
  id: string;
  signingSecret: string;
  signingSecretPrefix: string;
  signingSecretRotatedAt: string;
  overlapWindowDays: number;
}

export interface WebhookTestFireResponse {
  deliveryId: string;
  webhookId: string;
  event: string;
  status: 'queued' | 'success' | 'failed';
  statusCode: number | null;
  latencyMs: number | null;
  errorMessage: string | null;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useWebhooks() {
  return useQuery<WebhookListResponse, Error>({
    queryKey: ['admin-webhooks'],
    queryFn: () =>
      apiClient.get<WebhookListResponse>('/admin/settings/webhooks'),
  });
}

export function useWebhookDetail(id: string | null) {
  return useQuery<WebhookDetailResponse, Error>({
    queryKey: ['admin-webhook', id],
    queryFn: () =>
      apiClient.get<WebhookDetailResponse>(
        `/admin/settings/webhooks/${id}`
      ),
    enabled: !!id,
  });
}

export function useWebhookEventCatalog() {
  return useQuery<EventCatalogResponse, Error>({
    queryKey: ['admin-webhooks-event-catalog'],
    queryFn: () =>
      apiClient.get<EventCatalogResponse>(
        '/admin/settings/webhooks/events/catalog'
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export interface WebhookDeliveriesQuery {
  status?: WebhookDeliveryStatus;
  event?: string;
  limit?: number;
  offset?: number;
  since?: string;
  until?: string;
}

export function useWebhookDeliveries(
  webhookId: string | null,
  query: WebhookDeliveriesQuery = {}
) {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.event) params.set('event', query.event);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  if (query.since) params.set('since', query.since);
  if (query.until) params.set('until', query.until);
  const qs = params.toString();

  return useQuery<WebhookDeliveryListResponse, Error>({
    queryKey: ['admin-webhook-deliveries', webhookId, query],
    queryFn: () =>
      apiClient.get<WebhookDeliveryListResponse>(
        `/admin/settings/webhooks/${webhookId}/deliveries${qs ? `?${qs}` : ''}`
      ),
    enabled: !!webhookId,
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation<WebhookCreateResponse, Error, WebhookCreateBody>({
    mutationFn: (body) =>
      apiClient.post<WebhookCreateResponse>(
        '/admin/settings/webhooks',
        body
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-webhooks'] });
    },
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation<
    WebhookSummary,
    Error,
    { id: string; body: WebhookUpdateBody }
  >({
    mutationFn: ({ id, body }) =>
      apiClient.patch<WebhookSummary>(
        `/admin/settings/webhooks/${id}`,
        body
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-webhooks'] });
      qc.invalidateQueries({ queryKey: ['admin-webhook', vars.id] });
    },
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; deleted: true },
    Error,
    { id: string; confirmPhrase: string; reason: string }
  >({
    mutationFn: ({ id, confirmPhrase, reason }) =>
      apiClient.request(`/admin/settings/webhooks/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmPhrase, reason }),
        headers: { 'Content-Type': 'application/json' },
      }) as Promise<{ id: string; deleted: true }>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-webhooks'] });
    },
  });
}

export function useRotateWebhookSecret() {
  const qc = useQueryClient();
  return useMutation<
    WebhookRotateResponse,
    Error,
    { id: string; reason: string }
  >({
    mutationFn: ({ id, reason }) =>
      apiClient.post<WebhookRotateResponse>(
        `/admin/settings/webhooks/${id}/rotate-secret`,
        { reason }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-webhooks'] });
      qc.invalidateQueries({ queryKey: ['admin-webhook', vars.id] });
    },
  });
}

export function useTestFireWebhook() {
  const qc = useQueryClient();
  return useMutation<
    WebhookTestFireResponse,
    Error,
    { id: string; eventType?: string; samplePayload?: Record<string, unknown> }
  >({
    mutationFn: ({ id, eventType, samplePayload }) =>
      apiClient.post<WebhookTestFireResponse>(
        `/admin/settings/webhooks/${id}/test-fire`,
        { eventType, samplePayload }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-webhooks'] });
      qc.invalidateQueries({ queryKey: ['admin-webhook', vars.id] });
      qc.invalidateQueries({ queryKey: ['admin-webhook-deliveries', vars.id] });
    },
  });
}
